import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PackageSearch, ArrowLeft, ShieldCheck, Truck, RefreshCw, Check } from 'lucide-react';
import dholakImage from './assets/dholak.png';

function ProductDetails({ user, accessToken, onAddToCart, onRequireLogin, onAuthError }) {
  // Grab the dynamic product ID directly from the URL route path
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('Standard');
  
  // State for dynamic button interaction states ('idle', 'adding', or 'added')
  const [buttonFeedback, setButtonFeedback] = useState('idle');

  // Fetch the individual instrument details on mount or when ID changes
  useEffect(() => {
    console.log("=== ProductDetails Mount Debug ===");
    console.log("Extracted URL Param (id):", id);
    console.log("Base API URL:", process.env.REACT_APP_API_URL);

    if (!id || id === "undefined") {
      console.error("❌ CRITICAL: The product ID extracted from React Router is undefined or missing!");
      setLoading(false);
      return;
    }

    setLoading(true);
    const targetUrl = `${process.env.REACT_APP_API_URL}/api/products/products/${id}/`;
    console.log("Initiating Fetch Request To:", targetUrl);

    fetch(targetUrl)
      .then((res) => {
        console.log(`Response received with status code: ${res.status}`);
        if (!res.ok) {
          throw new Error(`Server responded with a bad status code: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log("Successfully fetched product payload data:", data);
        setProduct(data);
        if (data?.size) {
          setSelectedSize(data.size);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ Catch Block Triggered - Error loading product details:", err);
        setProduct(null);
        setLoading(false);
      });
  }, [id]);

  // Handle adding the premium asset to user cart with visual success confirmations
  const handleAddToCart = () => {
    if (!user) {
      if (onRequireLogin) {
        onRequireLogin();
      } else {
        console.warn("onRequireLogin prop missing on this route! Falling back to home redirect...");
        navigate('/login');
      }
      return;
    }
    if (!accessToken) return;

    if (!product || !product.id) {
      console.warn("Cannot add product: Product details data isn't loaded completely yet.");
      return;
    }

    setButtonFeedback('adding');

    // Values converted to strings to satisfy backend API requirements safely
    const payload = {
      product: String(product.id),
      quantity: "1"
    };

    console.log("Sending Formatted Payload:", payload);

    fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        console.log("Add to Cart API response status:", res.status);
        if (res.status === 401) {
          setButtonFeedback('idle');
          onAuthError && onAuthError();
          return;
        }
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          onAddToCart(product, data);
          
          setButtonFeedback('added');
          setTimeout(() => {
            setButtonFeedback('idle');
          }, 2000);
        } catch (e) {
          console.error("JSON parse failed on add to cart:", e);
          setButtonFeedback('idle');
        }
      })
      .catch((err) => {
        console.error('Add to cart error:', err);
        setButtonFeedback('idle');
      });
  };

  const getDiscountPercent = (price, finalPrice) => {
    if (!price || !finalPrice) return 0;
    return Math.round(((price - finalPrice) / price) * 100);
  };

  if (loading) {
    return (
      <div className="text-center py-40 text-[#A6A29A] animate-pulse uppercase tracking-widest text-xs bg-[#F2F1EC] min-h-screen flex items-center justify-center">
        Syncing Premium Instrument Details...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-20 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md max-w-sm mx-auto my-12 p-6 space-y-3 shadow-xs">
        <PackageSearch className="w-7 h-7 text-[#A6A29A] mx-auto" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#2C2A27]">Instrument Not Found</h3>
        <p className="text-[11px] text-[#8A867E] leading-relaxed font-light">
          The premium asset you are looking for might have been unlisted, sold, or moved to another category.
        </p>
        <Link to="/" className="inline-block pt-2 text-xs text-[#9A7B56] hover:text-[#2C2A27] font-semibold uppercase tracking-wider transition-colors">
          Back to Inventory
        </Link>
      </div>
    );
  }

  const productImageUri = !product.photo || product.photo === 'nan' || product.photo === 'null'
    ? dholakImage
    : product.photo;

  return (
    <section className="min-h-screen bg-[#F2F1EC] py-6 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Back Button Navigation */}
        <Link to={-1} className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-[#8A867E] hover:text-[#9A7B56] transition-colors mb-6 group">
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
          Back to Browse
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 bg-[#FDFDFB] border border-[#E1DFD7] p-4 sm:p-6 lg:p-8 rounded-md shadow-xs">

          {/* Left Side: Product Media Panel */}
          <div className="space-y-4">
            <div
              className="w-full aspect-square bg-contain bg-center bg-no-repeat rounded-md border border-[#E1DFD7] bg-[#FDFDFB]"
              style={{ backgroundImage: `url(${productImageUri})` }}
            />
          </div>

          {/* Right Side: Product Details & Operational Actions */}
          <div className="flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              {product.category && (
                <span className="text-[9px] bg-[#9A7B56]/10 text-[#9A7B56] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm border border-[#9A7B56]/20 inline-block">
                  {product.category}
                </span>
              )}

              <h1 className="text-xl sm:text-2xl font-light text-[#2C2A27] uppercase tracking-wide leading-tight mt-2">
                {product.product_name}
              </h1>

              {/* Financial Info & Pricing Blocks */}
              <div className="flex items-baseline gap-3 pt-1">
                <span className="text-2xl font-semibold text-[#2C2A27]">₹{product.final_price}</span>
                <span className="text-sm text-[#A6A29A] line-through font-light">₹{product.price}</span>
                <span className="text-[10px] font-bold text-[#9A7B56] bg-[#9A7B56]/5 px-2 py-0.5 border border-[#9A7B56]/20 rounded-xs uppercase tracking-wider">
                  {getDiscountPercent(product.price, product.final_price)}% OFF
                </span>
              </div>

              <hr className="border-[#E1DFD7] my-4" />

              {/* Instrument Specifications / Description */}
              <div className="space-y-2">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#A6A29A]">Product Highlights</h3>
                <p className="text-xs sm:text-sm text-[#5A5750] leading-relaxed font-light whitespace-pre-line">
                  {product.description || "Crafted to pristine technical standards, this premium musical instrument provides studio-ready performance metrics, authentic acoustic materials, and structural sound sustainability built for enthusiasts and veteran professionals alike."}
                </p>
              </div>

              {/* Optional Size Selector configuration */}
              {product.size_options && Array.isArray(product.size_options) && product.size_options.length > 0 && (
                <div className="space-y-2 pt-2">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#A6A29A]">Available Variations</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.size_options.map((sz) => (
                      <button
                        key={sz}
                        onClick={() => setSelectedSize(sz)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-all ${selectedSize === sz
                            ? 'border-[#9A7B56] bg-[#9A7B56]/5 text-[#9A7B56]'
                            : 'border-[#E1DFD7] bg-[#FDFDFB] text-[#8A867E] hover:border-[#A6A29A] hover:text-[#2C2A27]'
                          }`}
                      >
                        {sz}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Checkout Action Stack */}
            <div className="space-y-5 pt-4">
              <button
                onClick={handleAddToCart}
                disabled={buttonFeedback === 'adding'}
                className={`w-full font-semibold py-3.5 rounded-sm transition-all duration-300 uppercase tracking-widest text-[11px] active:scale-[0.99] flex items-center justify-center gap-2 border cursor-pointer ${
                  buttonFeedback === 'added'
                    ? 'bg-[#FDFDFB] text-[#2C2A27] border-[#E1DFD7] shadow-xs'
                    : 'bg-[#F2F1EC] hover:bg-[#9A7B56] text-[#5A5750] hover:text-white border-[#E1DFD7] hover:border-[#9A7B56]'
                }`}
              >
                {buttonFeedback === 'added' && <Check className="w-4 h-4 stroke-[3px] text-[#2C2A27]" />}
                {buttonFeedback === 'added' 
                  ? 'Added to Shopping Cart' 
                  : buttonFeedback === 'adding' 
                  ? 'Processing...' 
                  : 'Add to Shopping Cart'}
              </button>

              {/* Trust Badges & Logistics Assurances */}
              <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-[#E1DFD7]">
                <div className="p-2 space-y-1">
                  <Truck className="w-4 h-4 mx-auto text-[#9A7B56]/80" />
                  <p className="text-[10px] text-[#8A867E] font-medium uppercase tracking-wider">Insured Shipping</p>
                </div>
                <div className="p-2 space-y-1">
                  <ShieldCheck className="w-4 h-4 mx-auto text-[#9A7B56]/80" />
                  <p className="text-[10px] text-[#8A867E] font-medium uppercase tracking-wider">Secure Check</p>
                </div>
                <div className="p-2 space-y-1">
                  <RefreshCw className="w-4 h-4 mx-auto text-[#9A7B56]/80" />
                  <p className="text-[10px] text-[#8A867E] font-medium uppercase tracking-wider">Verified Quality</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductDetails;